/**
 * CartaoDaConversa — D2 (passo 14, 07/10/2026), peça E do desenho (§6.2 e §6.4).
 *
 * O miolo do aviso na tela quando TODOS os itens do grupo são mensagens da MESMA conversa: a
 * cara de quem escreveu, o autor (e o grupo), a prévia, "Conversa direta · agora" e a ação
 * "Silenciar" no lugar de "Lida" — mensagem não é notificação do sino, ler é abrir a conversa.
 * Mesma anatomia do `ConteudoDoAviso` do D1: corpo clicável = botão esticado ATRÁS do conteúdo
 * (botão dentro de botão é HTML inválido), ações e X por cima.
 *
 * Estados do cartão (o "Silenciar" troca o conteúdo no lugar, sem toast de outro sistema):
 *   normal → silenciando → silenciada ("Conversa silenciada." + Desfazer, fecha em 2 s)
 *                        ↘ erro ("Não silenciou." + Tentar de novo; sem relógio próprio: vale o de
 *                          8 s do aviso, que o Radix pausa só com o mouse ou o foco em cima)
 *   silenciada → desfazendo (o pedido de reativar; se falhar, volta a silenciada).
 *
 * ⚠️ O `ToastAction` do Radix FECHA o aviso ao ser clicado. Aqui o clique tem de trocar o
 *    conteúdo, não fechar: todo handler faz `preventDefault()` (o Radix só fecha se o evento
 *    não foi cancelado).
 * ⚠️ O botão da ação é o MESMO elemento em todos os estados (só muda o texto): a pessoa que
 *    chegou nele pelo teclado continua com o foco nele, agora dizendo "Desfazer".
 */
import { AtSign } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ChatRoomAvatar } from "@/components/chat/ChatRoomAvatar";
import { ToastAction, ToastClose, ToastDescription, ToastTitle } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import type { ChatRoom } from "@/types/chat";
import type { AcoesDoAviso, ConversaDoAviso, ItemDeConversa } from "./AvisoNaTela";
import { caminhoDaConversa, quandoDoAviso } from "./auxiliaresDoAviso";

/** Quanto o "Conversa silenciada." fica na tela antes de fechar sozinho. */
const FECHA_SILENCIADA_MS = 2000;

type Estado = "normal" | "silenciando" | "silenciada" | "desfazendo" | "erro";

function tituloDaConversa(c: ConversaDoAviso): string {
  return c.room_type === "DM" ? c.author_name : `${c.author_name} · ${c.sala}`;
}

/**
 * A "sala" que o avatar desenha é a do AUTOR, não a da conversa: uma DM sintética com
 * `peer_user_id = author_id`, que dá a foto (ou as iniciais) de quem escreveu — também no
 * grupo ("[AP] Ana Paula · Comercial Técnico"). O evento não traz o `ChatRoom` inteiro.
 */
function salaDoAutor(c: ConversaDoAviso): ChatRoom {
  return {
    id: c.room_id,
    name: c.author_name,
    room_type: "DM",
    sector_id: null,
    related_entity_type: null,
    related_entity_id: null,
    avatar_url: null,
    description: null,
    archived: false,
    created_by: c.author_id,
    created_at: "",
    updated_at: "",
    peer_user_id: c.author_id,
    peer_name: c.author_name,
  };
}

interface CartaoProps {
  itens: ItemDeConversa[];
  acoes: AcoesDoAviso;
  aoFechar: () => void;
}

export function CartaoDaConversa({ itens, acoes, aoFechar }: CartaoProps) {
  const navigate = useNavigate();
  const atual = itens[itens.length - 1];
  const conversa = atual.conversa;
  const mencao = conversa.mencao;

  const [estado, setEstado] = useState<Estado>("normal");
  const [total, setTotal] = useState(itens.length);
  // Mensagem nova com o cartão em "Conversa silenciada." / "Não silenciou.": o cartão volta a
  // mostrar a mensagem (uma menção atravessa o silêncio) e o relógio de 2 s deixa de valer.
  if (total !== itens.length) {
    setTotal(itens.length);
    setEstado("normal");
  }

  // Refs: o `aoFechar` muda de identidade a cada `update()`, e o resultado de um pedido em voo
  // não pode agir sobre uma mensagem que chegou depois do clique.
  const fechar = useRef(aoFechar);
  const totalAtual = useRef(itens.length);
  const emVoo = useRef(false);
  useEffect(() => {
    fechar.current = aoFechar;
    totalAtual.current = itens.length;
  });

  useEffect(() => {
    if (estado !== "silenciada") return;
    const relogio = setTimeout(() => fechar.current(), FECHA_SILENCIADA_MS);
    return () => clearTimeout(relogio);
  }, [estado]);

  const pedir = async (silenciar: boolean) => {
    if (emVoo.current) return;
    emVoo.current = true;
    const doClique = totalAtual.current;
    setEstado(silenciar ? "silenciando" : "desfazendo");
    try {
      await acoes.silenciarConversa?.(conversa.room_id, silenciar);
      if (totalAtual.current !== doClique) return;
      if (silenciar) setEstado("silenciada");
      else fechar.current();
    } catch {
      if (totalAtual.current !== doClique) return;
      // Desfazer que falha não muda o que o servidor tem: segue silenciada (e o relógio de 2 s
      // recomeça). Silenciar que falha pede para tentar de novo.
      setEstado(silenciar ? "erro" : "silenciada");
    } finally {
      emVoo.current = false;
    }
  };

  const abrir = () => {
    // O id de uma MENSAGEM não é de notificação e nunca vai para o `marcarLida`. O que vai é o da
    // linha de menção do sino que o contexto casou com a mensagem (`idDoSino`): quem abre a
    // conversa viu a menção, e a linha não pode ficar não lida (decisão de 03/10). Como no aviso
    // agrupado do D1, "Abrir" marca todas as do grupo, uma vez cada. Só o "Abrir" marca:
    // "Silenciar" não é ter visto a menção.
    const idsDoSino = new Set<string>();
    for (const a of itens) if (a.conversa.idDoSino) idsDoSino.add(a.conversa.idDoSino);
    for (const id of idsDoSino) acoes.marcarLida(id);
    navigate(caminhoDaConversa(conversa.room_id));
    aoFechar();
  };

  const mostraCorpo = estado === "normal" || estado === "silenciando";
  const titulo = tituloDaConversa(conversa);
  const tipo = mencao ? "Menção" : conversa.room_type === "DM" ? "Conversa direta" : "Grupo";
  const quando =
    itens.length > 1 ? `${itens.length} mensagens novas` : quandoDoAviso(atual.created_at);

  // A ação do rodapé, por estado. "Silenciar" não aparece quando a conversa já está silenciada
  // (a menção que atravessa o silêncio) nem quando o contexto não trouxe a ação.
  let acao: { texto: string; alt: string; aoClicar: () => void } | null = null;
  if (mostraCorpo) {
    if (acoes.silenciarConversa && !conversa.silenciada) {
      acao = {
        texto: "Silenciar",
        alt: "Silenciar esta conversa",
        aoClicar: () => void pedir(true),
      };
    }
  } else if (estado === "erro") {
    acao = {
      texto: "Tentar de novo",
      alt: "Tentar silenciar a conversa de novo",
      aoClicar: () => void pedir(true),
    };
  } else {
    acao = {
      texto: "Desfazer",
      alt: "Desfazer: voltar a receber os avisos da conversa",
      aoClicar: () => void pedir(false),
    };
  }

  // "Conversa silenciada." só depois da RESPOSTA do servidor: em voo (`silenciando`, também no
  // "Tentar de novo") o título segue o do autor, junto da prévia, e o aria-live não anuncia um
  // silêncio que pode falhar. Durante o Desfazer em voo o silêncio ainda existe: segue a frase.
  const mensagemDoEstado =
    estado === "erro" ? "Não silenciou." : mostraCorpo ? titulo : "Conversa silenciada.";

  return (
    <div className="group/aviso relative flex w-full gap-3 p-4 pr-10">
      {mostraCorpo && (
        <button
          type="button"
          aria-label={`Abrir: ${titulo}`}
          onClick={abrir}
          className="absolute inset-0 z-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      )}
      <span aria-hidden="true" className="pointer-events-none relative shrink-0">
        <ChatRoomAvatar room={salaDoAutor(conversa)} size={32} />
      </span>
      <div className="pointer-events-none relative min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          {mostraCorpo && mencao && (
            <AtSign
              aria-hidden="true"
              data-testid="aviso-mencao"
              className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300"
            />
          )}
          {/* aria-live: o leitor de tela anuncia a troca para "Conversa silenciada." — o Radix
              só anuncia o aviso quando ele nasce. */}
          <ToastTitle
            aria-live="polite"
            className={cn("truncate leading-5", mostraCorpo && "group-hover/aviso:underline")}
          >
            {mensagemDoEstado}
          </ToastTitle>
        </div>
        {mostraCorpo && atual.body && (
          <ToastDescription className="line-clamp-2 leading-5 text-foreground/80">
            {atual.body}
          </ToastDescription>
        )}
        {/* Sem flex-wrap (cicatriz do flex-wrap no desktop): o rótulo trunca, as ações não encolhem. */}
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="min-w-0 truncate">{mostraCorpo ? `${tipo} · ${quando}` : ""}</span>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <div className="pointer-events-auto relative z-10 flex items-center gap-1">
              {acao && (
                <ToastAction
                  altText={acao.alt}
                  aria-disabled={estado === "silenciando" || estado === "desfazendo"}
                  onClick={(e) => {
                    e.preventDefault(); // o Radix fecharia o aviso; aqui o clique troca o conteúdo
                    acao.aoClicar();
                  }}
                  className="h-11 px-3 text-xs sm:h-8"
                >
                  {acao.texto}
                </ToastAction>
              )}
            </div>
            {mostraCorpo && (
              <span aria-hidden="true" className="text-xs font-medium text-primary">
                Abrir ›
              </span>
            )}
          </div>
        </div>
      </div>
      <ToastClose aria-label="Fechar aviso" className="z-10 opacity-100 sm:opacity-0" />
    </div>
  );
}
